import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const retryTimerRef = useRef(null);

  const clearSession = useCallback(() => {
    setUser(null);
    setProfile(null);
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    try {
      localStorage.removeItem("obstuchim_token");
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith("expertconnect-auth") || key.startsWith("sb-")) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn("Failed to clear localStorage:", e);
    }
  }, []);

  const formatUserProfile = (userData) => {
    if (!userData) return null;
    const exp = userData.expertProfile || {};
    const lowerRole = (userData.role || "client").toLowerCase();

    return {
      id: String(userData.id),
      email: userData.email,
      name: userData.name || "Пользователь",
      role: lowerRole,
      avatar_url: userData.avatarUrl || exp.avatar || "",
      title: userData.position || exp.title || "",
      bio: userData.bio || exp.bio || "",
      hourly_rate: exp.hourlyRate || 5000,
      specializations: exp.specializations || [],
      skills: exp.skills || [],
      companies: exp.companies || (userData.company ? [{ name: userData.company, position: userData.position || "" }] : []),
      achievements: exp.achievements || [],
      available: true,
      expert_tier: exp.tier ? exp.tier.toLowerCase() : "pro",
      rating: exp.rating || 5.0,
      reviews_count: exp.reviewsCount || 0,
      phone: userData.phone || "",
      telegram: userData.telegram || "",
      linkedin: userData.linkedin || "",
      client_type: userData.position || "",
      job_title: userData.position || "",
      company_name: userData.company || "",
      company_website: "",
      company_size: "",
      company_description: "",
      industry: "",
      freelance_services: [],
      client_onboarding_completed: true,
      createdAt: userData.createdAt,
    };
  };

  const fetchProfile = useCallback(async (userId = null) => {
    const token = localStorage.getItem("obstuchim_token");
    if (!token) return null;

    try {
      const res = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.user) {
          setUser(data.user);
          const formatted = formatUserProfile(data.user);
          setProfile(formatted);
          return formatted;
        }
      } else if (res.status === 401 || res.status === 403) {
        clearSession();
      }
    } catch (err) {
      console.warn("[Auth] /api/auth/me fetch failed:", err);
    }
    return null;
  }, [clearSession]);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const token = localStorage.getItem("obstuchim_token");
        if (token) {
          const res = await fetch("/api/auth/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.user && mounted) {
              setUser(data.user);
              setProfile(formatUserProfile(data.user));
              setLoading(false);
              return;
            }
          } else {
            localStorage.removeItem("obstuchim_token");
          }
        }
      } catch (err) {
        console.warn("[Auth] Init auth failed:", err);
        if (mounted) {
          clearSession();
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      mounted = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, [clearSession]);

  // Вход по логину и паролю
  const login = async (email, password) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const json = await res.json();
    if (res.ok && json.token && json.user) {
      localStorage.setItem("obstuchim_token", json.token);
      setUser(json.user);
      const formatted = formatUserProfile(json.user);
      setProfile(formatted);
      return { user: json.user, profile: formatted };
    }

    throw new Error(json.error || "Неверный email или пароль");
  };

  // Быстрый демо-вход в 1 клик
  const demoLogin = async (role = "client") => {
    try {
      const res = await fetch("/api/auth/demo-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });

      const json = await res.json();
      if (res.ok && json.token && json.user) {
        localStorage.setItem("obstuchim_token", json.token);
        setUser(json.user);
        const formatted = formatUserProfile(json.user);
        setProfile(formatted);
        return { user: json.user, profile: formatted };
      }
    } catch (err) {
      console.warn("[Auth] Demo login fetch warning:", err);
    }

    // Fallback локального создания сессии
    const lowerRole = role.toLowerCase();
    const fallbackUser = {
      id: lowerRole === "expert" ? "1" : "demo-client-1",
      email: `${lowerRole}@obstuchim.online`,
      name: lowerRole === "expert" ? "Алексей Петров" : "Дмитрий Воронов",
      role: lowerRole.toUpperCase(),
      company: lowerRole === "expert" ? "Яндекс, Тинькофф" : "TechRetail Group",
      position: lowerRole === "expert" ? "Chief Marketing Officer" : "Операционный директор",
      createdAt: new Date().toISOString(),
    };

    localStorage.setItem("obstuchim_token", "demo-token-" + Date.now());
    setUser(fallbackUser);
    const formatted = formatUserProfile(fallbackUser);
    setProfile(formatted);
    return { user: fallbackUser, profile: formatted };
  };

  // Регистрация нового аккаунта
  const register = async (email, password, name, role, inviteCode, expertData) => {
    const serverRole = role?.toLowerCase() === "expert" ? "EXPERT" : "CLIENT";
    const res = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        name,
        role: serverRole,
        inviteCode,
        expertData,
      }),
    });

    const json = await res.json();
    if (res.ok && json.token && json.user) {
      localStorage.setItem("obstuchim_token", json.token);
      setUser(json.user);
      const formatted = formatUserProfile(json.user);
      setProfile(formatted);
      return {
        user: json.user,
        profile: formatted,
        session: { access_token: json.token },
      };
    }

    throw new Error(json.error || "Ошибка регистрации");
  };

  const saveConsents = async (userId, email, consents) => {
    // Безопасное сохранение согласий (не блокирует процесс)
    try {
      localStorage.setItem(`consents_${userId}`, JSON.stringify({ email, ...consents, at: Date.now() }));
    } catch (err) {
      console.warn("[Auth] Save consents warning:", err);
    }
  };

  const logout = async () => {
    const token = localStorage.getItem("obstuchim_token");
    if (token) {
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {}
    }
    clearSession();
  };

  const resetPassword = async (email) => {
    return { message: "Инструкция по восстановлению пароля отправлена на указанный email" };
  };

  const verifyEmailOtp = async (email, token) => {
    return { success: true };
  };

  const updateUser = async (updates) => {
    const token = localStorage.getItem("obstuchim_token");
    if (token) {
      try {
        const res = await fetch("/api/auth/profile", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updates),
        });
        if (res.ok) {
          const json = await res.json();
          if (json.user) {
            setProfile(formatUserProfile(json.user));
            setUser(json.user);
            return;
          }
        }
      } catch (err) {
        console.warn("[Auth] Update profile via Express API failed:", err);
      }
    }

    if (profile) {
      const merged = { ...profile, ...updates };
      setProfile(merged);
    }
  };

  const combinedUser = profile
    ? {
        id: profile.id,
        email: profile.email,
        name: profile.name,
        role: profile.role,
        avatar_url: profile.avatar_url || "",
        title: profile.title || "",
        bio: profile.bio || "",
        hourly_rate: profile.hourly_rate || 5000,
        specializations: profile.specializations || [],
        companies: profile.companies || [],
        achievements: profile.achievements || [],
        available: profile.available !== undefined ? profile.available : true,
        client_type: profile.client_type || "",
        job_title: profile.job_title || "",
        company_name: profile.company_name || "",
        company_website: profile.company_website || "",
        company_size: profile.company_size || "",
        company_description: profile.company_description || "",
        industry: profile.industry || "",
        freelance_services: profile.freelance_services || [],
        client_onboarding_completed: profile.client_onboarding_completed || false,
      }
    : null;

  return (
    <AuthContext.Provider
      value={{
        user: combinedUser,
        profile: combinedUser,
        supabaseUser: user || combinedUser,
        isAuthenticated: Boolean(combinedUser),
        login,
        demoLogin,
        register,
        logout,
        resetPassword,
        updateUser,
        verifyEmailOtp,
        saveConsents,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
